import React, { useState, useEffect } from 'react';
import axios from 'axios';
import LoadingComponent from '../../components/LoadingComponent';
import { API_BASE_URL } from "../../config";

const SubjectOverview = ({ subjectName, examType, keyConcepts }) => {
  const [progress, setProgress] = useState(0);
  const [subjectId, setSubjectId] = useState(null);
  const [loading, setLoading] = useState(true);

  const [nextPractice, setNextPractice] = useState(null);
  const [nextPracticeLoading, setNextPracticeLoading] = useState(false);

  const [activeStat, setActiveStat] = useState(0);

  const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
  const studentId = userInfo?.id;

  const [subjectStats, setSubjectStats] = useState({
    total_questions: 0,
    attempted_questions: 0,
    accuracy_percentage: 0,
    last_practiced_at: null,
  });

  const lastPracticedLabel = subjectStats.last_practiced_at
    ? new Date(subjectStats.last_practiced_at).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '—';

  useEffect(() => {
    if (!subjectId || !studentId) return;

    const fetchNextPractice = async () => {
      setNextPracticeLoading(true);
      try {
        const resp = await axios.get(`${API_BASE_URL}/api/subject_modules/next_practice`, {
          params: { subject_id: subjectId, student_id: studentId },
        });
        setNextPractice(resp.data);
      } catch (e) {
        console.error("Error fetching next practice module:", e);
        setNextPractice(null);
      } finally {
        setNextPracticeLoading(false);
      }
    };

    fetchNextPractice();
  }, [subjectId, studentId]);

  useEffect(() => {
    const fetchSubjectId = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/subjects`, {
          params: {
            subjectName: subjectName,
            examType: examType.toLowerCase(),
          },
        });

        const subject = response.data[0];
        if (subject) {
          setSubjectId(subject.id);
        } else {
          console.warn('No subject found for:', { subjectName, examType });
          setSubjectId(null);
          setProgress(0);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching subject id:', error);
        setSubjectId(null);
        setProgress(0);
        setLoading(false);
      }
    };

    setLoading(true);
    setProgress(0);
    fetchSubjectId();
  }, [subjectName, examType]);

  useEffect(() => {
    if (!subjectId || !studentId) return;

    const fetchSubjectMastery = async () => {
      try {
        const resp = await axios.get(`${API_BASE_URL}/api/subject_mastery_statistics`, {
          params: { student_id: studentId, subject_id: subjectId },
        });

        const pct = resp.data?.subject_mastery_percentage ?? 0;
        setProgress(Math.round(pct));

        setSubjectStats({
          total_questions: resp.data?.total_questions ?? 0,
          attempted_questions: resp.data?.attempted_questions ?? 0,
          accuracy_percentage: resp.data?.accuracy_percentage ?? 0,
          last_practiced_at: resp.data?.last_practiced_at ?? null,
        });
      } catch (error) {
        console.error('Error fetching subject mastery:', error);
        setProgress(0);
        setSubjectStats({
          total_questions: 0,
          attempted_questions: 0,
          accuracy_percentage: 0,
          last_practiced_at: null,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSubjectMastery();
  }, [subjectId, studentId]);

  const ctaLabel =
    progress === 100
      ? "Review last module"
      : subjectStats.attempted_questions > 0
        ? "Continue to Module"
        : "Start Module Quiz";

  const slugify = (s = "") =>
    String(s)
      .trim()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-");

  const examSlug = slugify(examType); // "BGCSE" -> "bgcse"
  const subjectSlug = slugify(subjectName);
  const frontEndUrl = window.location.origin;
  const SUBJECT_BASE_URL = `${frontEndUrl}/${examSlug}/${subjectSlug}`;

  const goToNextPractice = () => {
    if (!nextPractice?.module_name) return;

    const moduleSlug = slugify(nextPractice.module_name);

    if (subjectStats.attempted_questions > 0) {
      window.location.replace(`${SUBJECT_BASE_URL}/${moduleSlug}`);
    } else {
      window.location.replace(`${SUBJECT_BASE_URL}/${moduleSlug}/learn`);
    }
  };

  const statusMeta = (() => {
    if (progress === 0) return { label: 'Not Started', pill: 'bg-gray-100 text-gray-700 border-gray-200' };
    if (progress < 40) return { label: 'Getting Started', pill: 'bg-sky-50 text-sky-700 border-sky-200' };
    if (progress < 80) return { label: 'Building', pill: 'bg-amber-50 text-amber-700 border-amber-200' };
    if (progress < 100) return { label: 'Almost Ready', pill: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    return { label: 'Exam Ready', pill: 'bg-emerald-100 text-emerald-800 border-emerald-200' };
  })();

  const progressBarColor = progress === 100 ? 'bg-emerald-500' : 'bg-green-500';
  const pctAnchorClass = progress < 20 ? 'left-3' : 'right-3';

  if (loading) return <LoadingComponent />;

  return (
    <div>
      <div className="relative mt-0 py-5 px-5 my-5 w-full bg-[#f0f0f0]">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top,rgba(2,41,53,0.08),transparent_55%)]" />

        <div className="rounded-2xl border border-white/60 bg-white/70 backdrop-blur-md shadow-md overflow-hidden">
          {/* HERO HEADER */}
          <div className="px-6 pt-6 pb-4">

            <div className="flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                  <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-gray-500">
                    {examType}
                  </div>
                  <h1 className="mt-1 text-3xl sm:text-5xl font-semibold text-gray-900">
                    {subjectName}
                  </h1>
                </div>

                <div className="hidden sm:inline-flex flex items-center gap-2 sm:gap-3">
                  <span
                    className={` text-xs font-semibold px-3 py-1 rounded-full border ${statusMeta.pill}`}
                  >
                    {statusMeta.label}
                  </span>
                </div>

              </div>

              {/* Make this block full-width and aligned to the same left edge as heading */}
              <div className="w-full">
                <div className="mb-2 text-xs text-gray-500">
                  Exam Readiness
                </div>

                {/* Progress bar: rounded both sides, % clipped to green */}
                  <div className="w-full rounded-full border border-white/60 bg-white/40 py-2">
                    <div className="relative h-4 sm:h-5 w-full rounded-full overflow-hidden bg-gray-200/70 border border-gray-300/40">
                      {/* green fill */}
                      <div
                        style={{ width: `${progress}%` }}
                        className={`${progressBarColor} h-full rounded-full transition-all duration-300 relative overflow-hidden`}
                      >
                        {/* % label INSIDE green (won’t exceed green) */}
                        {progress > 0 && (
                          <span
                            className={`absolute top-1/2 -translate-y-1/2 ${progress < 35 ? 'left-3' : 'right-3'} text-white text-[10px] sm:text-xs font-semibold leading-none drop-shadow whitespace-nowrap`}
                            aria-hidden="true"
                          >
                            {progress}%
                          </span>
                        )}
                      </div>

                      {/* 0% label on the EMPTY track */}
                      {progress === 0 && (
                        <span
                          className="absolute top-1/2 -translate-y-1/2 left-3 text-gray-700 text-[10px] sm:text-xs font-semibold leading-none"
                          aria-hidden="true"
                        >
                          0%
                        </span>
                      )}
                    </div>
                  </div>
              </div>
            </div>
          </div>

          <div className="h-px bg-[#f0f0f0]" />

          {/* NEXT STEP CTA */}
          <div className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="min-w-0">
              <div className="text-xs sm:text-sm text-gray-600">Next step</div>

              <div
                className="
                  mt-1 text-base sm:text-lg font-semibold text-gray-900 leading-snug
                  whitespace-nowrap overflow-x-auto
                  [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
                "
              >
                {nextPracticeLoading
                  ? "Finding your next module…"
                  : nextPractice
                    ? nextPractice.module_name
                    : "We couldn’t find your next module."}
              </div>

              <div className="mt-1 text-xs sm:text-sm text-gray-600">
                {progress === 100
                  ? "You’ve mastered all modules — review the last one to stay sharp."
                  : "Continue along your recommended path."}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                onClick={goToNextPractice}
                disabled={!nextPractice?.module_name || nextPracticeLoading}
                className={`w-full sm:w-auto px-5 py-3 rounded-lg font-semibold shadow
                  ${(!nextPractice?.module_name || nextPracticeLoading)
                    ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                    : "bg-[#022935] text-white"}
                `}
              >
                {ctaLabel}
              </button>
            </div>
          </div>
        </div>

        {/* KEY CONCEPTS */}
        <div className="mt-6 rounded-2xl border border-white/60 bg-white/60 backdrop-blur-md shadow-md overflow-hidden">
          <div className="px-5 py-4 border-b border-white/90 flex items-center justify-between">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Key Concepts</h2>
          </div>

          <div className="px-5 py-4 bg-white/40">
            <ol className="font-thin text-gray-800 space-y-2 list-decimal list-inside">
              {keyConcepts.map((concept, index) => (
                <li key={index} className="leading-relaxed">
                  {concept}
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* STATS */}
        <div className="mt-6">
          {/* Mobile */}
          <div className="sm:hidden">
            <div className="rounded-2xl border border-white/60 bg-white/60 backdrop-blur-md shadow-md p-5 text-center">
              {activeStat === 0 && (
                <>
                  <div className="h-1.5 w-12 mx-auto rounded-full bg-[#022935]/80 mb-4" />
                  <p className="text-xs font-semibold tracking-wide text-gray-600 uppercase">
                    Questions Attempted
                  </p>
                  <p className="mt-2 text-3xl font-semibold text-gray-900">
                    {subjectStats.attempted_questions}
                    <span className="text-gray-400 text-xl"> / {subjectStats.total_questions}</span>
                  </p>
                </>
              )}

              {activeStat === 1 && (
                <>
                  <div className="h-1.5 w-12 mx-auto rounded-full bg-emerald-500/60 mb-4" />
                  <p className="text-xs font-semibold tracking-wide text-gray-600 uppercase">
                    Accuracy
                  </p>
                  <p className="mt-2 text-3xl font-semibold text-gray-900">
                    {subjectStats.accuracy_percentage}%
                  </p>
                </>
              )}

              {activeStat === 2 && (
                <>
                  <div className="h-1.5 w-12 mx-auto rounded-full bg-amber-500/60 mb-4" />
                  <p className="text-xs font-semibold tracking-wide text-gray-600 uppercase">
                    Last Practiced
                  </p>
                  <p className="mt-2 text-2xl font-semibold text-gray-900">
                    {lastPracticedLabel}
                  </p>
                </>
              )}
            </div>

            <div className="mt-3 flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => setActiveStat((s) => (s + 2) % 3)}
                className="text-sm text-gray-600 px-2 py-1 rounded-md hover:bg-white/50"
                aria-label="Previous stat"
              >
                ‹ Prev
              </button>

              <div className="flex items-center gap-2">
                {[0, 1, 2].map((i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActiveStat(i)}
                    className={`h-2.5 w-2.5 rounded-full transition ${
                      activeStat === i ? "bg-[#022935]" : "bg-[#022935]/30"
                    }`}
                    aria-label={`Show stat ${i + 1}`}
                  />
                ))}
              </div>

              <button
                type="button"
                onClick={() => setActiveStat((s) => (s + 1) % 3)}
                className="text-sm text-gray-600 px-2 py-1 rounded-md hover:bg-white/50"
                aria-label="Next stat"
              >
                Next ›
              </button>
            </div>
          </div>

          {/* Desktop */}
          <div className="hidden sm:grid grid-cols-3 gap-4">
            <div className="rounded-2xl border border-white/60 bg-white/60 backdrop-blur-md shadow-md p-5 text-center">
              <div className="h-1.5 w-12 mx-auto rounded-full bg-[#022935]/80 mb-4" />
              <p className="text-xs font-semibold tracking-wide text-gray-600 uppercase">
                Questions Attempted
              </p>
              <p className="mt-2 text-3xl font-semibold text-gray-900">
                {subjectStats.attempted_questions}
                <span className="text-gray-400 text-xl"> / {subjectStats.total_questions}</span>
              </p>
            </div>

            <div className="rounded-2xl border border-white/60 bg-white/60 backdrop-blur-md shadow-md p-5 text-center">
              <div className="h-1.5 w-12 mx-auto rounded-full bg-emerald-500/60 mb-4" />
              <p className="text-xs font-semibold tracking-wide text-gray-600 uppercase">
                Accuracy
              </p>
              <p className="mt-2 text-3xl font-semibold text-gray-900">
                {subjectStats.accuracy_percentage}%
              </p>
            </div>

            <div className="rounded-2xl border border-white/60 bg-white/60 backdrop-blur-md shadow-md p-5 text-center">
              <div className="h-1.5 w-12 mx-auto rounded-full bg-amber-500/60 mb-4" />
              <p className="text-xs font-semibold tracking-wide text-gray-600 uppercase">
                Last Practiced
              </p>
              <p className="mt-2 text-3xl font-semibold text-gray-900">
                {lastPracticedLabel}
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default SubjectOverview;
